import { useEffect, useRef } from 'react';
import { useAnalytics } from '@/services/analytics';
import type { SuggestionState } from './hook';

type ImpressionTrackingOptions = {
  isHidden: boolean;
  clickLocation: string;
};

/** Tracks one `suggestionsDisplayed` impression per distinct rendered chip set. */
export function useSuggestionImpressionTracking(
  state: SuggestionState,
  { isHidden, clickLocation }: ImpressionTrackingOptions,
) {
  const { track } = useAnalytics();
  const { isVisible, suggestions, routeGroup, source } = state;
  const lastImpressionRef = useRef<string | null>(null);

  useEffect(
    function trackSuggestionImpression() {
      // `isVisible` is already gated on a non-empty list by useSuggestions.
      if (isHidden || !isVisible) {
        return;
      }

      const labels = suggestions.map((suggestion) => suggestion.label);
      // Chips re-render on every path change; key the impression on what was
      // actually shown so a repaint of the same set isn't counted twice.
      const impressionKey = `${routeGroup}|${source}|${labels.join('|')}`;
      if (lastImpressionRef.current === impressionKey) {
        return;
      }
      lastImpressionRef.current = impressionKey;

      void track('aiAssistant', 'suggestionsDisplayed', {
        'Click Location': clickLocation,
        'Route Group': routeGroup,
        'Suggestion Source': source,
        'Suggestion Labels': labels,
        Count: labels.length,
      });
    },
    [isHidden, isVisible, suggestions, routeGroup, source, clickLocation, track],
  );
}
